import {GetterTree} from "vuex";
import {RootState} from "@service/store/types/RootState";
import {ExpertRatingsState} from "@service/store/types/ExpertRatingsState";
import Vue from 'vue';
import { Project } from '@share/expert-ratings/types/Project';
import { Element } from '@api/models/Element';
import { UserProfile } from '@api/models/UserProfile';
import { DropDownItem } from '@share/common/drop-down/types';
import { showDataErrorModal, getUnknownProfileErrorMessage } from "@share/expert-ratings/helpers/wrongDataHandler";

const getters: GetterTree<ExpertRatingsState, RootState> = {
    selectedSection: state => {
        return state.sections.find(item => item.id == state.selected);
    },
    selectedSectionId: state => state.selected,
    sections: state => state.sections,
    sectionListForDropDown: (_, getters) => {
        return getters['sections'].sort((a: Element, b: Element) => {
            if (a.title < b.title) { return -1; }
            if (a.title > b.title) { return 1; }
            return 0;
        })
    },
    profile: state => (id: string) => state.profiles.find(profile => profile.id === id),
    sectionProjects: (state: ExpertRatingsState): Project[] => state.sectionProjects,
    criterias: state => state.criterias,
    additionalCriterias: state => state.additionalCriterias,
    sectionRatings: state => (sectionId: string): Record<string, (number|null)[]> => {
        return Object.prototype.hasOwnProperty.call(state.ratings, sectionId)
            ? state.ratings[sectionId]
            : Vue.observable({})
    },
    projectRatings: state => (projectId: string) => {
       return (state.selected && state.ratings[state.selected])
            ? state.ratings[state.selected][projectId]
            : null
    },
    canSaveCurrentSectionRates: state => state.canSaveCurrentSectionRates,
    allActiveCriteriasCount: state => state.criterias.length + state.additionalCriterias.filter(item => item && item.title).length,
    currentSectionRatesSaved: state => state.currentSectionRatesSaved,
    expertRate: state => (projectId: string, expertId: string) => {
        if (!Object.prototype.hasOwnProperty.call(state.summaryExpertRatings, projectId)) {
            return null;
        }

        return state.summaryExpertRatings[projectId][expertId] ?? null;
    },
    experts: (state, getters, __, rootGetters) => {
        let emptyProfileIds: string[] = [];

        const profiles = getters.selectedSection?.userProfileIds
            .filter((id: string) => {
                const profile = getters.profile(id);
                if (!profile && rootGetters['isAdmin'] ) {
                    emptyProfileIds.push(id);
                }
                return id !== getters.selectedSection?.chairmanProfileId && profile;
            })
            .map((id: string) => new UserProfile(getters.profile(id)));

        if (emptyProfileIds.length && !state.profileErrorShown) {
            state.profileErrorShown = true;
            showDataErrorModal(getUnknownProfileErrorMessage(emptyProfileIds.join(", ")));
        }

        return profiles;
    },
    expertsListForDropDown: (state, getters, __, rootGetters) => (title: string) => {
        const allExperts: DropDownItem = {
            id: undefined,
            title
        }
        let emptyProfileIds: string[] = [];

        const list = getters.selectedSection?.userProfileIds
            .filter((id: string) => {
                const profile = getters.profile(id);
                if (!profile && rootGetters['isAdmin']) {
                    emptyProfileIds.push(id);
                }
                return profile
            })
            .map((id: string) => new UserProfile(getters.profile(id)))
            .map((item: DropDownItem | UserProfile) => {
                if (item.id) {
                    item.title = getters.profile(item.id).fullName;
                }
                return item;
            });

        if (emptyProfileIds.length && !state.profileErrorShown) {
            state.profileErrorShown = true;
            showDataErrorModal(getUnknownProfileErrorMessage(emptyProfileIds.join(", ")));
        }

        return list ? [allExperts, ...list] : [allExperts];
    },
    summaryExpertComment: state => (projectId: string, expertId: string) => {
        if (!Object.prototype.hasOwnProperty.call(state.summaryExpertsComments, projectId)) {
            return null;
        }

        return state.summaryExpertsComments[projectId][expertId] ?? null;
    },
    projectRate: state => (projectId: string) => {
        return state.summaryProjectRatings[projectId] ?? null;
    },
    expertComments: state => (expertId: string) => {
        return state.comments[expertId] ?? {};
    },
    summaryComments: state => state.summaryComments,
    summaryProjectRatings: state => state.summaryProjectRatings
};

export {getters}